// src/components/admin/page-sections/FeaturedProductsSection.js
import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import LazyImage from "../../LazyImage";

const FeaturedProductsSection = ({ content }) => {
  const {
    title = "Featured Products",
    showTitle = true,
    showSeparator = true,
    searchEnabled = false,
    searchPlaceholder = "Search featured products...",
    maxProducts = 5,
    gridColumns = {
      mobile: 1,
      tablet: 2,
      desktop: 3,
      widescreen: 5,
    },
    showProductDescription = false,
    showPricing = true,
    showViewAllButton = true,
    viewAllButtonText = "View All Products",
    viewAllButtonLink = "/products",
    viewAllButtonColor = "#033568",
    viewAllButtonTextColor = "#ffffff",
    imageAspectRatio = "100%",
    backgroundColor = "#ffffff",
    titleColor = "#111827", // text-gray-900
    separatorColor = "#9CA3AF", // text-gray-400
    cardBorderEnabled = true,
    cardBorderColor = "#E5E7EB", // border-gray-200
    cardShadowEnabled = true,
    priceColor = "#2563EB", // text-blue-600
    hoverEffectEnabled = true,
    apiPath = "/api/products/basic",
    marginTop = "4rem",
    marginBottom = "8rem",
    gapSize = "1.5rem",
  } = content;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch featured products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch basic product data
        const response = await fetch(apiPath);
        const productsData = await response.json();

        // Filter for featured products only
        const featuredProducts = productsData.filter(
          (product) => product.isFeatured
        );
        setProducts(featuredProducts);
        setLoading(false);

        // Fetch images for the featured products
        const productImagePromises = featuredProducts.map((product) =>
          fetch(`/api/products/${product._id}/image`)
            .then((res) => res.json())
            .then((imageData) => {
              setProducts((prev) =>
                prev.map((p) =>
                  p._id === product._id
                    ? {
                        ...p,
                        images: [
                          {
                            _id: imageData._id,
                            path: imageData.path || null,
                            thumbnail: imageData.thumbnail || null,
                            data: imageData.data || null,
                          },
                        ],
                      }
                    : p
                )
              );
            })
            .catch((error) =>
              console.error(
                `Error loading image for product ${product._id}:`,
                error
              )
            )
        );

        // Load all images in parallel
        await Promise.allSettled(productImagePromises);
      } catch (error) {
        console.error("Error fetching featured products:", error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [apiPath]);

  // Navigate to product detail page
  const navigateToProduct = (product) => {
    const match = product.name.match(/^(.*?)(\d.*)?$/);
    let baseName = match[1] ? match[1].trim() : product.name;
    let sizePart = match[2] ? match[2].trim() : "";

    const slugBase = baseName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    const slugSize = sizePart;
    const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

    window.location.href = `/product/${productSlug}`;
  };

  // Filter products based on search term
  const filteredProducts = products
    .filter(
      (product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, maxProducts);

  // Calculate grid column classes based on configuration
  const getGridColumns = () => {
    // Handle both string and object formats for gridColumns
    if (!gridColumns || typeof gridColumns === "string") {
      return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
    }

    // If we have a proper object with responsive breakpoints
    const mobileCol = gridColumns.mobile || 1;
    const tabletCol = gridColumns.tablet || 2;
    const desktopCol = gridColumns.desktop || 3;
    const widescreenCol = gridColumns.widescreen || 4;

    return `grid-cols-${mobileCol} sm:grid-cols-${tabletCol} md:grid-cols-${desktopCol} lg:grid-cols-${widescreenCol}`;
  };

  // Calculate card classes based on configuration
  const getCardClasses = () => {
    let classes = "rounded-lg overflow-hidden transition-transform";

    if (hoverEffectEnabled) {
      classes += " hover:scale-105";
    }

    if (cardShadowEnabled) {
      classes += " shadow-lg";
    }

    if (cardBorderEnabled) {
      classes += " border border-t-1 border-t-gray-300";
    }

    return classes;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center">
        <svg
          className="animate-spin h-8 w-8 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          ></path>
        </svg>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor, marginTop, marginBottom }}>
      {/* Title Section with Separator */}
      {showTitle && (
        <div className="container mx-auto px-4 my-16">
          {showSeparator ? (
            <div className="flex items-center">
              <div
                className="flex-grow border-t"
                style={{ borderColor: separatorColor }}
              ></div>
              <span
                className="mx-4 uppercase tracking-widest text-sm"
                style={{ color: separatorColor }}
              >
                <h2
                  className="text-3xl font-serif font-bold"
                  style={{ color: titleColor }}
                >
                  {title}
                </h2>
              </span>
              <div
                className="flex-grow border-t"
                style={{ borderColor: separatorColor }}
              ></div>
            </div>
          ) : (
            <h2
              className="text-3xl font-serif font-bold text-center mb-8"
              style={{ color: titleColor }}
            >
              {title}
            </h2>
          )}
        </div>
      )}

      {/* Search Bar */}
      {searchEnabled && (
        <div className="container mx-auto px-4 mb-8">
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="container mx-auto px-4">
        <div
          className={`grid ${getGridColumns()} gap-6`}
          style={{ gap: gapSize || "1.5rem" }}
        >
          {filteredProducts.map((product) => (
            <div
              key={product._id}
              className={getCardClasses()}
              style={{
                borderColor: cardBorderEnabled
                  ? cardBorderColor
                  : "transparent",
              }}
            >
              <div
                className="relative cursor-pointer"
                onClick={() => navigateToProduct(product)}
                style={{ paddingBottom: imageAspectRatio }}
              >
                <LazyImage
                  // src={product.images?.[0]?.data}
                  src={
                    product.images?.[0]?.path
                      ? `/api${product.images[0].path}`
                      : product.images?.[0]?.data
                  }
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div
                className="p-4 cursor-pointer"
                onClick={() => navigateToProduct(product)}
              >
                <h3 className="text-md font-bold text-gray-900 mb-2 truncate">
                  {product.name}
                </h3>
                {showProductDescription && (
                  <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                    {product.description}
                  </p>
                )}
                {showPricing && (
                  <div className="flex justify-between items-center">
                    <span
                      className="text-m font-semibold"
                      style={{ color: priceColor }}
                    >
                      $
                      {product.pricingTiers && product.pricingTiers.length > 0
                        ? product.pricingTiers[
                            product.pricingTiers.length - 1
                          ].price.toFixed(2)
                        : product.basePrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {filteredProducts.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-500">
              No featured products found.
            </div>
          )}
        </div>

        {/* View All Button */}
        {showViewAllButton && filteredProducts.length > 0 && (
          <div className="text-center mt-20">
            <button
              onClick={() => (window.location.href = viewAllButtonLink)}
              className="px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
              style={{
                backgroundColor: viewAllButtonColor,
                color: viewAllButtonTextColor,
              }}
            >
              {viewAllButtonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductsSection;

// // src/components/admin/page-sections/FeaturedProductsSection.js
// import React, { useState, useEffect } from 'react';
// import { Search } from 'lucide-react';
// import LazyImage from '../../LazyImage';

// const FeaturedProductsSection = ({ content }) => {
//   const {
//     title = 'Featured Products',
//     showTitle = true,
//     showSeparator = true,
//     searchEnabled = false,
//     searchPlaceholder = 'Search featured products...',
//     maxProducts = 5,
//     gridColumns = {
//       mobile: 1,
//       tablet: 2,
//       desktop: 3,
//       widescreen: 5
//     },
//     showProductDescription = false,
//     showPricing = true,
//     showViewAllButton = true,
//     viewAllButtonText = 'View All Products',
//     viewAllButtonLink = '/products',
//     viewAllButtonColor = '#033568',
//     viewAllButtonTextColor = '#ffffff',
//     imageAspectRatio = '100%',
//     backgroundColor = '#ffffff',
//     titleColor = '#111827', // text-gray-900
//     separatorColor = '#9CA3AF', // text-gray-400
//     cardBorderEnabled = true,
//     cardBorderColor = '#E5E7EB', // border-gray-200
//     cardShadowEnabled = true,
//     priceColor = '#2563EB', // text-blue-600
//     hoverEffectEnabled = true,
//     apiPath = '/api/products/basic',
//     marginTop = '4rem',
//     marginBottom = '8rem',
//     gapSize = '1.5rem'
//   } = content;

//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState('');

//   // Fetch featured products
//   useEffect(() => {
//     const fetchProducts = async () => {
//       try {
//         // Fetch basic product data
//         const response = await fetch(apiPath);
//         const productsData = await response.json();

//         // Filter for featured products only
//         const featuredProducts = productsData.filter(product => product.isFeatured);
//         setProducts(featuredProducts);
//         setLoading(false);

//         // Fetch images for the featured products
//         const productImagePromises = featuredProducts.map(product =>
//           fetch(`/api/products/${product._id}/image`)
//             .then(res => res.json())
//             .then(imageData => {
//               setProducts(prev => prev.map(p =>
//                 p._id === product._id ? { ...p, images: [imageData] } : p
//               ));
//             })
//             .catch(error => console.error(`Error loading image for product ${product._id}:`, error))
//         );

//         // Load all images in parallel
//         await Promise.allSettled(productImagePromises);
//       } catch (error) {
//         console.error('Error fetching featured products:', error);
//         setLoading(false);
//       }
//     };

//     fetchProducts();
//   }, [apiPath]);

//   // Navigate to product detail page
//   const navigateToProduct = (product) => {
//     const match = product.name.match(/^(.*?)(\d.*)?$/);
//     let baseName = match[1] ? match[1].trim() : product.name;
//     let sizePart = match[2] ? match[2].trim() : '';

//     const slugBase = baseName
//       .toLowerCase()
//       .replace(/\s+/g, '-')
//       .replace(/[^a-z0-9-]/g, '');

//     const slugSize = sizePart;
//     const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

//     window.location.href = `/product/${productSlug}`;
//   };

//   // Filter products based on search term
//   const filteredProducts = products
//     .filter(product =>
//       product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       product.description.toLowerCase().includes(searchTerm.toLowerCase())
//     )
//     .slice(0, maxProducts);

//   // Calculate grid column classes based on configuration
//   const getGridColumns = () => {
//     return `grid-cols-${gridColumns.mobile} sm:grid-cols-${gridColumns.tablet} md:grid-cols-${gridColumns.desktop} xl:grid-cols-${gridColumns.widescreen}`;
//   };

//   // Calculate card classes based on configuration
//   const getCardClasses = () => {
//     let classes = 'rounded-lg overflow-hidden transition-transform';

//     if (hoverEffectEnabled) {
//       classes += ' hover:scale-105';
//     }

//     if (cardShadowEnabled) {
//       classes += ' shadow-lg';
//     }

//     if (cardBorderEnabled) {
//       classes += ' border border-t-1 border-t-gray-300';
//     }

//     return classes;
//   };

//   if (loading) {
//     return (
//       <div className="container mx-auto px-4 py-8 flex justify-center items-center">
//         <svg className="animate-spin h-8 w-8 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
//         </svg>
//       </div>
//     );
//   }

//   return (
//     <div style={{ backgroundColor, marginTop, marginBottom }}>
//       {/* Title Section with Separator */}
//       {showTitle && (
//         <div className="container mx-auto px-4 my-16">
//           {showSeparator ? (
//             <div className="flex items-center">
//               <div className="flex-grow border-t" style={{ borderColor: separatorColor }}></div>
//               <span className="mx-4 uppercase tracking-widest text-sm" style={{ color: separatorColor }}>
//                 <h2 className="text-3xl font-serif font-bold" style={{ color: titleColor }}>
//                   {title}
//                 </h2>
//               </span>
//               <div className="flex-grow border-t" style={{ borderColor: separatorColor }}></div>
//             </div>
//           ) : (
//             <h2 className="text-3xl font-serif font-bold text-center mb-8" style={{ color: titleColor }}>
//               {title}
//             </h2>
//           )}
//         </div>
//       )}

//       {/* Search Bar */}
//       {searchEnabled && (
//         <div className="container mx-auto px-4 mb-8">
//           <div className="relative max-w-md mx-auto">
//             <input
//               type="text"
//               placeholder={searchPlaceholder}
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="w-full px-4 py-2 pl-10 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
//             />
//             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
//           </div>
//         </div>
//       )}

//       {/* Products Grid */}
//       <div className="container mx-auto px-4">
//         <div
//           className={`grid ${getGridColumns()} gap-6`}
//           style={{ gap: gapSize }}
//         >
//           {filteredProducts.map((product) => (
//             <div
//               key={product._id}
//               className={getCardClasses()}
//               style={{ borderColor: cardBorderEnabled ? cardBorderColor : 'transparent' }}
//             >
//               <div
//                 className="relative cursor-pointer"
//                 onClick={() => navigateToProduct(product)}
//                 style={{ paddingBottom: imageAspectRatio }}
//               >
//                 <LazyImage
//                   src={product.images?.[0]?.data}
//                   alt={product.name}
//                   className="absolute inset-0 w-full h-full object-cover"
//                 />
//               </div>
//               <div
//                 className="p-4 cursor-pointer"
//                 onClick={() => navigateToProduct(product)}
//               >
//                 <h3 className="text-md font-bold text-gray-900 mb-2 truncate">
//                   {product.name}
//                 </h3>
//                 {showProductDescription && (
//                   <p className="text-gray-600 mb-4 text-sm line-clamp-2">
//                     {product.description}
//                   </p>
//                 )}
//                 {showPricing && (
//                   <div className="flex justify-between items-center">
//                     <span className="text-m font-semibold" style={{ color: priceColor }}>
//                       ${ product.pricingTiers && product.pricingTiers.length > 0 ?
//                         product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                         product.basePrice.toFixed(2) }
//                     </span>
//                   </div>
//                 )}
//               </div>
//             </div>
//           ))}

//           {filteredProducts.length === 0 && (
//             <div className="col-span-full text-center py-8 text-gray-500">
//               No featured products found.
//             </div>
//           )}
//         </div>

//         {/* View All Button */}
//         {showViewAllButton && filteredProducts.length > 0 && (
//           <div className="text-center mt-20">
//             <button
//               onClick={() => window.location.href = viewAllButtonLink}
//               className="px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
//               style={{
//                 backgroundColor: viewAllButtonColor,
//                 color: viewAllButtonTextColor
//               }}
//             >
//               {viewAllButtonText}
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default FeaturedProductsSection;
